import { cx } from "@jugl-web/utils";
import { FC, Fragment, ReactNode } from "react";
import { PlainButton } from "../../primitives";
import { ReactComponent as ArrowLeftIcon } from "./assets/arrow-left.svg";
import { ReactComponent as ChevronRightIcon } from "./assets/chevron-right.svg";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { DrawerHeaderNavButton } from "./components/DrawerHeaderNavButton";

export interface TitleDrawerHeaderProps {
  type: "title";
  title: ReactNode;
  subtitle?: ReactNode;
  endSlot?: ReactNode;
  onGoBack?: () => void;
}

export interface Breadcrumb {
  id: string;
  title: ReactNode;
  subtitle?: ReactNode;
  onClick?: () => void;
}

export interface BreadcrumbsDrawerHeaderProps {
  type: "breadcrumbs";
  breadcrumbs: Breadcrumb[];
  endSlot?: ReactNode;
}

interface BaseDrawerHeaderProps {
  navigationSlots?: ReactNode[];
  onClose: () => void;
}

export type DrawerHeaderProps = BaseDrawerHeaderProps &
  (TitleDrawerHeaderProps | BreadcrumbsDrawerHeaderProps);

export const DRAWER_HEADER_HEIGHT_PX = 60;

export const DrawerHeader: FC<DrawerHeaderProps> = ({
  navigationSlots = [],
  onClose,
  ...props
}) => (
  <header
    className="relative z-10 flex shrink-0 items-center justify-between border-t-0 border-r-0 border-b border-l-0 border-solid border-dark-100 px-8 max-lg:px-5"
    style={{ height: DRAWER_HEADER_HEIGHT_PX }}
  >
    {props.type === "title" && (
      <div className="flex items-center gap-2.5">
        {props.onGoBack && (
          <PlainButton onClick={props.onGoBack}>
            <ArrowLeftIcon />
          </PlainButton>
        )}
        <div className="flex items-center gap-2 font-secondary">
          <span className="max-w-[370px] truncate text-sm leading-4 font-semibold tracking-[0.16px] text-[#383838]">
            {props.title}
          </span>
          {props.subtitle && (
            <span className="text-sm font-normal tracking-[0.14px] text-grey-700">
              {props.subtitle}
            </span>
          )}
        </div>
      </div>
    )}
    {props.type === "breadcrumbs" && (
      <div className="flex items-center gap-4">
        {props.breadcrumbs.map((breadcrumb, index, array) => {
          const isLast = index === array.length - 1;
          const isInteractive = !!breadcrumb.onClick;

          return (
            <Fragment key={breadcrumb.id}>
              <div className="flex items-center gap-2">
                <span
                  className={cx(
                    "max-w-[250px] truncate text-base leading-4 tracking-[0.16px] text-dark",
                    isLast && "font-semibold",
                    isInteractive && "cursor-pointer"
                  )}
                  {...(isInteractive
                    ? { onClick: breadcrumb.onClick, role: "button" }
                    : undefined)}
                >
                  {breadcrumb.title}
                </span>
                {breadcrumb.subtitle && (
                  <span className="text-sm font-normal tracking-[0.14px] text-grey-700">
                    {breadcrumb.subtitle}
                  </span>
                )}
              </div>
              {!isLast && <ChevronRightIcon />}
            </Fragment>
          );
        })}
      </div>
    )}
    <div className="flex items-center gap-6">
      {navigationSlots.length > 0 &&
        navigationSlots.map((slot, index) => (
          <Fragment key={+index}>{slot}</Fragment>
        ))}
      {props.endSlot}
      <DrawerHeaderNavButton onClick={onClose}>
        <CloseIcon />
      </DrawerHeaderNavButton>
    </div>
  </header>
);
